import {useParams} from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {CloudUpload, Delete} from '@mui/icons-material';
import {useCallback, useEffect, useState} from 'react';
import {AxiosResponse} from 'axios';
import {
  IRejectedSongRequestProp,
  useRqRejectedSongRequestDetails,
} from '../../../../../react_query/song-requests/SongRequests';
import {IMediaTrack} from '../../../../../dto/MediaTrack.dto';
import {useStylesed} from '../../../../../components/styles/commonStyled';
import MusicSelectionTable from '../../../../../components/music/MusicSelectionTable';
import {iTimedMessageMessageItem} from '../../../../messages/timed-message/request/contents/TimedMsgRequestContentMessageCard';
import {
  broadCastCloseTab,
  useRqTaskInfo,
} from '../../../../../react_query/tasks/ListTasks';
import DialogApiRequestStatus from '../../../../../components/dialogs/DialogApiRequestStatus';
import {apiPost} from '../../../../../axiosConfig';
import {IApiSuccessMsgResponse} from '../../../../../dto/API.dto';
import AudioDurationValidation, {
  getAudioDurationFromFile,
} from '../../../../../components/utlis/Audio/GetAudioDuration';
import MusicPreviewBtn from '../../../../../components/music/MusicPreviewBtn';

const SongFileReupload = () => {
  const taskParam = useParams();
  const taskId = Number(taskParam.task_id);
  const {
    data: taskData,
    isFetching: taskIsFetching,
    isError: taskIsError,
  } = useRqTaskInfo(taskId);
  const [companyId, setCompanyId] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const {data, isFetching, isError} = useRqRejectedSongRequestDetails(taskId);

  const handleCancel = useCallback(() => {
    broadCastCloseTab();
  }, []);

  useEffect(() => {
    if (taskData && !taskIsFetching && !taskIsError) {
      setCompanyId(taskData.company?.id ?? 0);
    }
  }, [taskData, taskIsError, taskIsFetching]);

  return (
    <Box className='m-2 p-3 blue-variant17'>
      <CardContent>
        {data && !isFetching && !isError ? (
          <RejectedSongHistoryTable data={data} />
        ) : null}
      </CardContent>
      <Box className='flex justify-end gap-2'>
        <Button variant='contained' color='warning' onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant='contained' onClick={() => setOpen(true)}>
          Replace File(s)
        </Button>
      </Box>
      <SongSelectionDialogBox
        open={open}
        setOpen={setOpen}
        companyId={companyId}
      />
    </Box>
  );
};

export default SongFileReupload;

const RejectedSongHistoryTable = ({
  data,
}: {
  data: IRejectedSongRequestProp[];
}) => {
  return (
    <TableContainer component={Paper}>
      <Table aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell>File</TableCell>
            <TableCell>File Name</TableCell>
            <TableCell>Rejected Note</TableCell>
            <TableCell>Rejected Description</TableCell>
            <TableCell>Rejected By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => {
            const producerFile = JSON.parse(
              row.song_request.processing_center.file_path,
            );
            return (
              <TableRow key={index}>
                <TableCell>
                  {producerFile.file_url ? (
                    <MusicPreviewBtn
                      id={producerFile.file_url}
                      url={producerFile.file_url}
                      track_name={producerFile.file_name ?? ''}
                      track_artist={'Uploaded file'}
                    />
                  ) : null}
                </TableCell>
                <TableCell component='th' scope='row'>
                  {row.file_name}
                </TableCell>
                <TableCell>{row.final_review_notes}</TableCell>
                <TableCell>
                  {row.no_play_list_ratings.map(value => {
                    return <Box>{value.short_description}</Box>;
                  })}
                </TableCell>
                <TableCell>{row.rejected_by.name}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const defaultSongSelection: iTimedMessageMessageItem = {
  id: -1 * Date.now(),
  isNew: true,
  type: 'message',
  message: '',
  artist: '',
  title: '',
};
export const SongSelectionDialogBox = ({
  open,
  setOpen,
  companyId,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  companyId: number;
}) => {
  const taskParam = useParams();
  const taskId = Number(taskParam.task_id);
  const [songSelection, setSongSelection] =
    useState<iTimedMessageMessageItem>(defaultSongSelection);
  const handleClose = () => {
    setOpen(false);
  };
  const [apiState, setApiState] = useState({
    status: '',
    error: null,
    successMsg: '',
  });
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null, successMsg: ''});
  }, []);
  const handleSubmit = useCallback(() => {
    setApiState({status: 'pending', error: null, successMsg: ''});
    const fd = new FormData();
    if (songSelection.file) {
      fd.append('file', songSelection.file);
    } else if (songSelection.track?.id) {
      fd.append('track_id', songSelection.track?.id.toString());
    }
    apiPost(`v1/task/${taskId}/song-request-rejected?_method=PUT`, fd)
      .then((response: AxiosResponse<IApiSuccessMsgResponse>) => {
        setApiState({
          status: 'success',
          error: null,
          successMsg: response.data.message,
        });
        broadCastCloseTab();
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
          successMsg: '',
        });
      });
  }, [songSelection.file, songSelection.track?.id, taskId]);
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        maxWidth='lg'>
        <DialogTitle id='alert-dialog-title'>Song Selection</DialogTitle>
        <DialogContent>
          <TimedMsgRequestSongCard
            songSelection={songSelection}
            setSongSelection={setSongSelection}
            companyId={companyId}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} autoFocus>
            Submit
          </Button>
          <Button onClick={handleClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSubmit}
          onEdit={handleEdit}
        />
      )}
    </>
  );
};
type Props = {
  songSelection: iTimedMessageMessageItem;
  setSongSelection: React.Dispatch<
    React.SetStateAction<iTimedMessageMessageItem>
  >;
  companyId: number;
};
// timed message files
const TimedMsgRequestSongCard = ({
  songSelection,
  setSongSelection,
  companyId,
}: Props) => {
  const classes = useStylesed();

  const handleSelect = (track: IMediaTrack) => {
    setSongSelection(prev => {
      return {...prev, file: undefined, track, fileDuration: 0};
    });
  };
  const handleDeselect = () => {
    setSongSelection(prev => {
      return {
        ...prev,
        file: undefined,
        track: undefined,
        fileDuration: 0,
      };
    });
  };
  return (
    <Card>
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={9}>
            <MusicSelectionTable
              selectedTracks={songSelection.track ? [songSelection.track] : []}
              onSelect={handleSelect}
              onDeselect={handleDeselect}
              isAddDisabled={false}
              company_id={companyId}
            />
          </Grid>
          <Grid item xs={3}>
            {songSelection.track ? (
              <SelectedSongCmpt track={songSelection.track} />
            ) : (
              <Box sx={{height: '100%'}} className={classes.c_even}>
                {songSelection.artist || songSelection.title ? null : (
                  <SongUploadCmpt
                    songSelection={songSelection}
                    setSongSelection={setSongSelection}
                  />
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

function SelectedSongCmpt({track}: Readonly<{track: IMediaTrack}>) {
  const classes = useStylesed();
  return (
    <Stack
      spacing={4}
      sx={{
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Box className={classes.c_center}>
        <Typography variant='h6'>Title</Typography>
        <Typography>{track?.track_name}</Typography>
      </Box>
      <Box className={classes.c_center}>
        <Typography variant='h6'>Artist</Typography>
        <Typography>{track?.track_artist}</Typography>
      </Box>
    </Stack>
  );
}

type iSongUploadProps = {
  songSelection: iTimedMessageMessageItem;
  setSongSelection: React.Dispatch<
    React.SetStateAction<iTimedMessageMessageItem>
  >;
};

function SongUploadCmpt({
  songSelection,
  setSongSelection,
}: Readonly<iSongUploadProps>) {
  const classes = useStylesed();
  const [fileDuration, setFileDuration] = useState(0);

  const handleOnChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target?.files && e.target.files.length > 0) {
        const file = e.target.files[0] as File;
        const duration = await getAudioDurationFromFile(file);
        setFileDuration(duration);
        setSongSelection(prev => {
          return {
            ...prev,
            track: undefined,
            file,
            fileDuration: duration,
          };
        });
      }
    },
    [setSongSelection],
  );

  const handleRemove = () => {
    setFileDuration(0);
    setSongSelection(prev => {
      return {
        ...prev,
        file: undefined,
        fileDuration: 0,
      };
    });
  };

  return (
    <Stack className={classes.c_center} spacing={2} mt={2}>
      <Typography>
        Upload a song from your computer. We will review it.
      </Typography>
      <AudioDurationValidation
        durationInSec={fileDuration}
        maxDurationInMin={5}
      />
      {songSelection.file ? (
        <Typography>
          {songSelection.file.name}{' '}
          <IconButton onClick={handleRemove}>
            <Delete />
          </IconButton>
        </Typography>
      ) : null}
      <Button component='label' variant='contained' startIcon={<CloudUpload />}>
        Upload Song
        <input
          accept='audio/*'
          type='file'
          id='select-voice'
          style={{
            display: 'none',
          }}
          value=''
          onChange={handleOnChange}
        />
      </Button>
    </Stack>
  );
}
